import {
    type AuctionItemEntity,
    type AuctionListingItemEntity,
    type DealerSaleItemEntity,
    type DealerSaleListingItemEntity,
    type DirectSaleItemEntity,
    type DirectSaleListingItemEntity,
    type OfflineSaleItemEntity,
    type OfflineSaleListingItemEntity,
    type AuctionNegotiationPlatformEntity,
    SaleType,
    SellingMethod,
} from '~/apiClient';

export type PlatformSellingMethod = Extract<SellingMethod, SellingMethod.Auction | SellingMethod.DirectSale>;

/** Auction + DirectSale only — listing pages exist only for these two */
export type PlatformSaleListingItemEntity = AuctionListingItemEntity | DirectSaleListingItemEntity;

/** All selling methods */
export type SaleListingItemEntity = PlatformSaleListingItemEntity | OfflineSaleListingItemEntity | DealerSaleListingItemEntity;

/** Auction + DirectSale only — for pages/components that have public detail routes */
export type PlatformSaleItemEntity = AuctionItemEntity | DirectSaleItemEntity;

/** All selling methods — for order pages and account flows */
export type SaleItemEntity = PlatformSaleItemEntity | OfflineSaleItemEntity | DealerSaleItemEntity;

export enum SaleBundleType {
    Mono = SaleType.Mono,
    BundleMonoVersion = SaleType.BundleMonoVersion,
    BundleMultiVersion = SaleType.BundleMultiVersion,
}

export const SALE_BUNDLE_TYPE_TO_SALE_TYPE_ARRAY_MAP = {
    [SaleBundleType.Mono]: [SaleType.Mono, SaleType.MonoToScrap],
    [SaleBundleType.BundleMonoVersion]: [SaleType.BundleMonoVersion, SaleType.BundleMultiVersionToScrap],
    [SaleBundleType.BundleMultiVersion]: [SaleType.BundleMultiVersion, SaleType.BundleMultiVersionToScrap],
};

export function isAuction(sale: SaleItemEntity | SaleListingItemEntity): sale is AuctionItemEntity | AuctionListingItemEntity {
    return sale.sellingMethod === SellingMethod.Auction;
}

export function isDirectSale(sale: SaleItemEntity | SaleListingItemEntity): sale is DirectSaleListingItemEntity | DirectSaleItemEntity {
    return sale.sellingMethod === SellingMethod.DirectSale;
}

export function isPlatformSale(sale: SaleItemEntity | SaleListingItemEntity): sale is PlatformSaleItemEntity | PlatformSaleListingItemEntity {
    return isAuction(sale) || isDirectSale(sale);
}

export function isOfflineSale(sale: SaleItemEntity | SaleListingItemEntity): sale is OfflineSaleItemEntity | OfflineSaleListingItemEntity {
    return sale.sellingMethod === SellingMethod.OfflineSale;
}

export function isDealerSale(sale: SaleItemEntity | SaleListingItemEntity): sale is DealerSaleItemEntity | DealerSaleListingItemEntity {
    return sale.sellingMethod === SellingMethod.DealerSale;
}

export function isPlatformSaleListingItemEntity(
    sale: PlatformSaleItemEntity | SaleItemEntity | SaleListingItemEntity
): sale is PlatformSaleListingItemEntity {
    return !('assets' in sale);
}

export interface AuctionItemEntityWithNegotiation extends Omit<AuctionItemEntity, 'negotiation'> {
    negotiation: AuctionNegotiationPlatformEntity;
}

export const isAuctionItemEntityWithNegotiation = (auction: AuctionItemEntity): auction is AuctionItemEntityWithNegotiation =>
    auction.negotiation !== null;
